import { useState, useEffect, useRef } from "preact/compat";
import { html } from "htm/preact/index.js";
import "src/components/calendar/calender.js";
import { disablePopupEscapeClose } from "src/services/popup-escape-guard";
import { formatLocalDateForDisplay } from "src/services/helpers.js";
import SvgIcon from "../svg-icon/svg-icon";

/**
 * @typedef {object} FieldBoxProps
 * @property {string} icon
 * @property {string} inlineSvg
 * @property {string} id
 * @property {string} label
 * @property {import("../query-form/query-form-utils").QueryFormOption[]} options
 * @property {function} onChange
 * @property {string} type
 * @property {string} value
 * @property {string} placeholder
 * @param {FieldBoxProps} props
 * @returns {import("preact").VNode}
 */
function FieldBox({ icon, inlineSvg, id, label, onChange, options = [], type, value, placeholder  }) {
    const [isOpen, setOpen] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const [isFocused, setIsFocused] = useState(false);
    const [isKeyboardFocus, setIsKeyboardFocus] = useState(false);
    const containerRef = useRef(null);
    const listRef = useRef(null);
    const buttonRef = useRef(null);
    const restorePopupEscapeRef = useRef(null);
    const selectedOption = options.find(opt => opt.value === value);

    const handleSelect = (opt) => (event) => {
        event.stopPropagation();
        event.preventDefault();
        setOpen(false);
        if (!opt.disabled) onChange(opt.value);
    };

    const handleOpen = (event) => {
        event.stopPropagation();
        setOpen(prev => {
            if (!prev && type !== "date") {
                const selectedIndex = options.findIndex(opt => opt.value === value);
                setFocusedIndex(selectedIndex >= 0 ? selectedIndex : 0);
            }else {
                setFocusedIndex(-1);
            }
            return !prev;
        });
    };

    const handleKeyDown = (event) => {
        // Handle Space, Enter to open dropdown
        if (
            event.key === " " ||
            event.key === "Enter" ||
            event.key === "ArrowDown"
            ) {
            event.preventDefault();
            event.stopPropagation();
            if (!isOpen) {
                const selectedIndex = options.findIndex(opt => opt.value === value);
                const index = selectedIndex >= 0 ? selectedIndex : 0;
            
                setOpen(true);
                setFocusedIndex(index);
            } else if (focusedIndex >= 0 && type !== "date") {
                // Select focused option if dropdown is open
                const opt = options[focusedIndex];
                if (!opt.disabled) {
                    onChange(opt.value);
                    setOpen(false);
                    setFocusedIndex(-1);
                }
            }
        }
        // Handle Escape to close dropdown
        else if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            setOpen(false);
            setFocusedIndex(-1);
        }
        // Handle Arrow Down - navigate to next option
        else if (event.key === "ArrowDown" && isOpen && type !== "date") {
            event.preventDefault();
            event.stopPropagation();
            let nextIndex = focusedIndex + 1;
            if (nextIndex >= options.length) nextIndex = 0;
            setFocusedIndex(nextIndex);
        }
        // Handle Arrow Up - navigate to previous option
        else if (event.key === "ArrowUp" && isOpen && type !== "date") {
            event.preventDefault();
            event.stopPropagation();
            let prevIndex = focusedIndex - 1;
            if (prevIndex < 0) prevIndex = options.length - 1;
            setFocusedIndex(prevIndex);
        }
    };

    const handleDateSelect = (selectedDate) => {
        onChange(selectedDate);

        if (selectedDate.keepOpenOnSelect) {
            return;
        }

        setOpen(false);
        setFocusedIndex(-1);
        requestAnimationFrame(() => {
            if (buttonRef.current instanceof HTMLElement) {
                buttonRef.current.focus();
            }
        });
    };

    const handleCalendarClose = () => {
        setOpen(false);
        setFocusedIndex(-1);
    };


    const handleContainerBlur = () => {
        requestAnimationFrame(() => {
            const active = document.activeElement;
    
            if (
                containerRef.current &&
                !containerRef.current.contains(active)
            ) {
                setOpen(false);
                setFocusedIndex(-1);
            }
        });
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!containerRef.current.contains(event.target)) {
                setOpen(false);
                setFocusedIndex(-1);
            }
        };
        
        
        // Track keyboard vs mouse interactions
        const handleKeyDown = (event) => {
            if (event.key === "Tab" || event.key === "Enter" || event.key === " " || 
                event.key === "ArrowUp" || event.key === "ArrowDown") {
                setIsKeyboardFocus(true);
            }
        };
        
        const handleMouseDown = () => {
            setIsKeyboardFocus(false);
        };
        
        document.addEventListener("click", handleClickOutside);
        document.addEventListener("keydown", handleKeyDown);
        document.addEventListener("mousedown", handleMouseDown);
        
        return () => {
            document.removeEventListener("click", handleClickOutside);
            document.removeEventListener("keydown", handleKeyDown);
            document.removeEventListener("mousedown", handleMouseDown);
        };
    }, []);

    useEffect(() => {
        if (restorePopupEscapeRef.current) {
            restorePopupEscapeRef.current();
            restorePopupEscapeRef.current = null;
        }

        if (isOpen && type === "date") {
            restorePopupEscapeRef.current = disablePopupEscapeClose(containerRef.current);
        }

        return () => {
            if (restorePopupEscapeRef.current) {
                restorePopupEscapeRef.current();
                restorePopupEscapeRef.current = null;
            }
        };
    }, [isOpen, type]);

    // Scroll focused option into view when index changes
    useEffect(() => {
        if (!isOpen || type === "date" || focusedIndex < 0) {
            return;
        }
    
        requestAnimationFrame(() => {
            const focusedElement = listRef.current?.querySelector(
                `.mx-fieldbox__dropdown-item:nth-child(${focusedIndex + 1})`
            );
    
            if (focusedElement instanceof HTMLElement) {
                focusedElement.focus();
    
                focusedElement.scrollIntoView({
                    block: "nearest"
                });
            }
        });
    }, [focusedIndex, isOpen, type]);

    return html`
        <label
            class="mx-fieldbox"
            ref=${containerRef}
        >
            <span class="mx-fieldbox__label">${label}</span>

            <button
                ref=${buttonRef}
                id=${id}
                type="button"
                class=${"mx-fieldbox__button" + (isFocused && isKeyboardFocus ? " mx-fieldbox__button--focused" : "")}
                aria-expanded=${isOpen}
                aria-haspopup=${type === "date" ? "dialog" : "listbox"}
                aria-controls=${isOpen ? `${id}-dropdown` : undefined}
                aria-describedby=${type === "date" ? `${id}-description` : undefined}
                aria-label=${value
                    ? `Change ${label}, ${
                        type === "date"
                            ? formatLocalDateForDisplay(value)
                            : selectedOption?.name || value
                      }`
                    : label
                }
                onClick=${handleOpen}
                onKeyDown=${handleKeyDown}
                onFocus=${() => {
                    setIsFocused(true);
                    setIsKeyboardFocus(true);
                }}
                onBlur=${() => {
                    setIsFocused(false);
                    setIsKeyboardFocus(false);
                }}
                style=${isFocused && isKeyboardFocus ? {
                    outline: '2px solid #173F35',
                    outlineOffset: '2px'
                } : {}}
            >
                <span class="mx-fieldbox__value-wrapper">
                    ${value
                        ? type === "date"
                            ? formatLocalDateForDisplay(value)
                            : (selectedOption?.name || value)
                        : html`<span class="mx-fieldbox__placeholder">
                            ${placeholder || "Select"}
                        </span>`}
                    
                    ${inlineSvg
                        ? html`<span class="mx-fieldbox__icon" dangerouslySetInnerHTML=${{ __html: inlineSvg }}></span>`
                        : html`<${SvgIcon}
                                    classes="mx-fieldbox__icon"
                                    icon=${icon}
                            />`}
                    </span>
            </button>

            ${type === "date" && 
                html`<div id=${`${id}-description`} class="sr-only">
                    Date format: Month Day, Year (e.g., January 1, 2024)
                </div>`}

            ${isOpen && type === "date" &&
                html`
                    <div
                    id=${`${id}-dropdown`}
                    class="mx-fieldbox__dropdown"
                    ref=${listRef}
                >
                        <mx-reservation-calendar
                                value=${value ?? ""}
                                onDateChanged=${handleDateSelect}
                                onCalendarClose=${handleCalendarClose}
                        ></mx-reservation-calendar>
                    </div>`}

            ${isOpen && type !== "date" && options.length > 0 &&
                html`
                    <ul 
                    id=${`${id}-dropdown`}
                    class="mx-fieldbox__dropdown"
                    ref=${listRef}
                    role="listbox"
                    aria-labelledby=${id}
                    >
                        ${options.map((opt, index) => html`
                            <li
                                key=${opt.value}
                                role="option"
                                tabIndex=${focusedIndex === index ? 0 : -1}
                                aria-disabled=${opt.disabled ? "true" : "false"}
                                aria-selected=${opt.value === value}
                                class=${"mx-fieldbox__dropdown-item " + (opt.disabled ? "disabled" : "") + (focusedIndex === index ? " focused" : "")}
                                onClick=${handleSelect(opt)}
                                onKeyDown=${(event) => {
                                    switch (event.key) {
                                        case "ArrowDown":
                                            event.preventDefault();
                                            setFocusedIndex((focusedIndex + 1) % options.length);
                                            break;
                                
                                        case "ArrowUp":
                                            event.preventDefault();
                                            setFocusedIndex(
                                                focusedIndex === 0
                                                    ? options.length - 1
                                                    : focusedIndex - 1
                                            );
                                            break;
                                
                                        case "Enter":
                                        case " ":
                                            console.log("Selecting", opt.value);
                                        
                                            event.preventDefault();
                                            event.stopPropagation();
                                        
                                            if (!opt.disabled) {
                                                    onChange(opt.value);
                                                    setOpen(false);
                                                    setFocusedIndex(-1);
                                        
                                                requestAnimationFrame(() => {
                                                    buttonRef.current?.focus();
                                                });
                                            }
                                            break;
                                
                                        case "Escape":
                                            event.preventDefault();
                                            setOpen(false);
                                            setFocusedIndex(-1);
                                
                                            requestAnimationFrame(() => {
                                                buttonRef.current?.focus();
                                            });
                                            break;
                                    }
                                }}
                                style=${isKeyboardFocus && focusedIndex === index ? {
                                    outline: '2px solid #173F35',
                                    outlineOffset: '-2px'
                                } : {}}
                            >
                                <span class="mx-fieldbox__option-label">
                                    ${opt.name}
                                </span>
                            </li>`
                        )}
                    </ul>`}
        </label>
    `;
}

export default FieldBox;
